import { FieldArray, FieldArrayRenderProps, Input, Toggle } from 'components/form';
import { YES_NO_OPTIONS } from 'constants/form';
import { useFormSubField } from 'hooks/form';
import isEqual from 'lodash/isEqual';
import { _HormonalTherapyInput } from 'models/endpoint';
import React from 'react';
import { useTranslation } from 'react-i18next';
import AddItem from '../AddItem';
import RemoveItem from '../RemoveItem';

export type HormonalTherapy = _HormonalTherapyInput;
export const DEFAULT_HORMONAL_THERAPY: HormonalTherapy = {
  id: undefined,
  hormonal_therapy: '',
  regimen: '',
  duration: '',
  date_to: '',
  date_from: '',
  institution: ''
};

interface HormonalTherapyItemProps {
  index: number;
  helpers: FieldArrayRenderProps;
  showAdd: boolean;
  showRemove: boolean;
  onChangeCb: (data?: any) => void;
}

const HormonalTherapyItem: React.FC<HormonalTherapyItemProps> = React.memo(
  ({ index, helpers, showAdd, showRemove, onChangeCb }) => {
    const [t] = useTranslation('patient_profile');
    const { getSubName } = useFormSubField('hormonal_therapies');
    const insert = () => helpers.push(DEFAULT_HORMONAL_THERAPY);
    const remove = () => {
      helpers.remove(index);
      onChangeCb();
    };
    return (
      <React.Fragment>
        <div className="flex-row">
          {index === 0 ? (
            <div className="flex-child">
              <Toggle
                name={getSubName('hormonal_therapy', index)}
                items={YES_NO_OPTIONS}
                onChange={onChangeCb}
                fullWidth
              />
            </div>
          ) : null}
          <div className="flex-child" style={{ flexGrow: index === 0 ? 3 : 1 }}>
            <div className="flex-row">
              <div className="flex-child">
                <Input type="date" label={t('from')} name={getSubName('date_from', index)} fullWidth />
              </div>
              <div className="flex-child">
                <Input type="date" label={t('to')} name={getSubName('date_to', index)} fullWidth />
              </div>
            </div>
          </div>
        </div>
        <div className="flex-row">
          <div className="flex-child">
            <Input label={t('regimen')} name={getSubName('regimen', index)} fullWidth />
          </div>
          <div className="flex-child">
            <Input label={t('duration_of_each')} name={getSubName('duration', index)} fullWidth />
          </div>
        </div>
        <div className="flex-row">
          <Input label={t('institution')} name={getSubName('institution', index)} fullWidth />
        </div>
        <div className="add-multiple">
          {showAdd ? <AddItem onClick={insert} /> : null}
          {showRemove ? <RemoveItem onClick={remove} /> : null}
        </div>
      </React.Fragment>
    );
  }
);

interface Props {
  list: HormonalTherapy[];
  onChangeCb: (data?: any) => void;
}

const HormonalTherapy: React.FC<Props> = props => {
  const [t] = useTranslation('patient_profile');
  const { getName } = useFormSubField('hormonal_therapies');
  const { list, onChangeCb } = props;
  const totalItems = list.length;
  const lastItemIndex = totalItems - 1;
  return (
    <div className="section">
      <label className="sub-header grey">{t('hormonal_therapy', { count: 1 })}</label>
      <FieldArray
        name={getName()}
        render={helpers =>
          list.map((_, index: number) => (
            <HormonalTherapyItem
              key={index}
              index={index}
              helpers={helpers}
              showAdd={totalItems === 1 || (totalItems > 1 && lastItemIndex === index)}
              showRemove={index > 0 && lastItemIndex === index}
              onChangeCb={onChangeCb}
            />
          ))
        }
      />
    </div>
  );
};

export default React.memo(HormonalTherapy, (prev, next) => isEqual(prev.list, next.list));
